<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';

const props = defineProps({
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  component: { type: Function, default: null },
  badgeCount: { type: [Number, String], default: 0 },
  hideTreeLine: { type: Boolean, default: false },
  thinTreeLine: { type: Boolean, default: false },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});

// Tree-line connector per leaf: vertical line (::before) + rounded elbow on the
// last child (::after). Logical props (start / border-s / rounded-es)
const TREE_CONNECTOR =
  "child-item before:content-[''] before:absolute before:start-0 before:w-0.5 before:h-full before:bg-n-slate-4 first:before:rounded-t last:before:h-1/5 last:after:content-[''] last:after:absolute last:after:start-0 last:after:bottom-[calc(50%_-_2px)] last:after:h-3 last:after:w-2.5 last:after:border-b-2 last:after:border-s-2 last:after:rounded-es last:after:border-n-slate-4";
</script>

<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="py-0.5 ps-2 ms-3 relative text-n-slate-11 min-w-0 group/leaf"
    :class="{
      [TREE_CONNECTOR]: !hideTreeLine,
      'before:!w-px last:after:!border-b last:after:!border-s':
        !hideTreeLine && thinTreeLine,
    }"
  >
    <!-- Modern vertical line indicator -->
    <div
      class="absolute ltr:left-0 rtl:right-0 top-0 bottom-0 w-px bg-border/40 group-hover/leaf:bg-primary/40 transition-colors"
    />

    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      :title="label"
      class="flex h-8 items-center gap-2.5 px-3.5 py-1 rounded-xl transition-all duration-200 group min-w-0 relative"
      :class="{
        'text-primary bg-primary/10 font-semibold active': active,
        'text-muted-foreground hover:text-foreground hover:bg-accent/20':
          !active,
      }"
    >
      <div
        v-if="active"
        class="absolute ltr:left-1.5 rtl:right-1.5 top-1/2 -translate-y-1/2 w-[3px] h-3.5 bg-primary rounded-full"
      />
      <component
        :is="component"
        v-if="shouldRenderComponent"
        v-bind="{ label, icon, active, badgeCount }"
      />
      <template v-else>
        <span
          v-if="icon"
          class="size-4 flex items-center justify-center rounded-full opacity-70 group-hover:opacity-100 transition-opacity"
        >
          <Icon :icon="icon" class="size-3.5" />
        </span>
        <div
          class="flex-1 truncate min-w-0 text-xs font-medium"
          :class="{ 'ltr:pl-1 rtl:pr-1': active }"
        >
          {{ label }}
        </div>
        <SidebarUnreadBadge :count="badgeCount" />
      </template>
    </component>
  </Policy>
</template>
